/**
 * HeroCreator — builder pierwszego wrażenia.
 *
 * Filozofia (APISAI_HERO_CREATOR_SPEC_v1):
 *   - To NIE jest builder stron. To "first-impression builder".
 *   - Preview jest najważniejszy. Wszystko inne służy preview.
 *   - Mobile-safe automation pilnowane przez engine (HeroPreview).
 *   - Quick Mode default, Advanced odsłania extra opcje (gradient/alignment/font size/overlay/CTA style).
 *
 * Layout (3-col): Steps sidebar (left) · Live preview (center) · Templates + Controls (right).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import {
  Image as ImageIcon, Type as TypeIcon, Palette, Sparkles, Upload,
  Monitor, Smartphone, Wand2, Check, Save, Eye, ArrowLeft, ArrowRight, RotateCcw, Loader2, X,
  Move, ZoomIn, ZoomOut, Maximize2, Crosshair,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import StudioShell from '@/components/studio/StudioShell';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';
import HeroPreview from '@/components/hero-creator/HeroPreview';
import HeroCreatorIntro from '@/components/hero-creator/HeroCreatorIntro';
import NextStepGuide from '@/components/NextStepGuide';

/* ─── DEFAULT CONFIG ─── */
const DEFAULT_CONFIG = {
  image_url: '',
  logo_url: '',
  label: 'HODOWLA Z PASJĄ',
  headline: 'Twoja Hodowla',
  subheading: 'Zdrowe psy. Szczęśliwe rodziny. Na całe życie.',
  cta_primary: { label: 'Zobacz nasze psy', href: '#psy' },
  cta_secondary: { label: 'Skontaktuj się', href: '#kontakt' },
  style: {
    template_id: 'loyal_heart',
    overlay: 'gradient_bottom',
    overlay_strength: 65,
    text_align: 'left',
    headline_font: 'montserrat-extrabold',
    headline_size: 72,
    sub_size: 22,
    label_size: 14,
    accent_color: '#F97316',
    cta_style: 'filled',
    image_position_x: 50,
    image_position_y: 50,
    image_zoom: 1.0,
  },
  tags: [
    { icon: 'shield', text: 'Rodowód FCI' },
    { icon: 'dog', text: 'Badania genetyczne' },
    { icon: 'home', text: 'Domowa hodowla' },
  ],
};

/* ─── STEPS ─── */
const STEPS = [
  { id: 1, key: 'image', title: 'Wybierz zdjęcie', sub: 'Wgraj lub wybierz tło', Icon: ImageIcon },
  { id: 2, key: 'text', title: 'Dodaj tekst', sub: 'Nagłówek, podtytuł, CTA', Icon: TypeIcon },
  { id: 3, key: 'style', title: 'Dopasuj styl', sub: 'Styl + opcje zaawansowane', Icon: Palette },
  { id: 4, key: 'finish', title: 'Podgląd i zapisz', sub: 'Telefon + komputer', Icon: Sparkles },
];

const FONT_OPTIONS = [
  { id: 'montserrat-extrabold', label: 'Montserrat ExtraBold (nowoczesny)' },
  { id: 'montserrat-bold', label: 'Montserrat Bold (czysty)' },
  { id: 'playfair-bold', label: 'Playfair Bold (premium serif)' },
];

const OVERLAY_OPTIONS = [
  { id: 'gradient_bottom', label: 'Dolny cień', preview: 'linear-gradient(to top, #000 0%, transparent 70%)' },
  { id: 'dark', label: 'Dark', preview: 'rgba(0,0,0,0.55)' },
  { id: 'gradient_navy', label: 'Navy', preview: 'linear-gradient(135deg, #00121F 0%, #003A56 100%)' },
  { id: 'gradient_orange', label: 'Orange', preview: 'linear-gradient(135deg, #281208 0%, #F97316 100%)' },
  { id: 'none', label: 'Bez nakładki', preview: 'transparent' },
];

const ACCENT_COLORS = ['#F97316', '#00273D', '#10B981', '#0EA5E9', '#D4AF37', '#EC4899', '#FFFFFF', '#1F2937'];

const TAG_PRESETS = [
  { icon: 'shield', text: 'Rodowód FCI' },
  { icon: 'dog', text: 'Badania genetyczne' },
  { icon: 'home', text: 'Domowa hodowla' },
  { icon: 'paw', text: 'Socjalizacja' },
  { icon: 'heart', text: 'Wsparcie po sprzedaży' },
  { icon: 'star', text: 'Champion bloodline' },
  { icon: 'medal', text: 'Linia użytkowa' },
  { icon: 'sparkles', text: 'Standard premium' },
];

/* ─── HELPERS ─── */
function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/* ─── MAIN ─── */
const CREATION_TYPE_KEY = 'apisai_creation_type';
const CREATION_TYPES_HODOWCA = [
  { id: 'kennel', title: 'Hodowla', tagline: 'Pełny profil', desc: 'Psy, mioty, historia, galeria.', wizard: '/wizard/hodowla-v2', thumb: 'https://images.unsplash.com/photo-1576201836106-db1758fd1c97?w=200&q=80&auto=format&fit=crop' },
  { id: 'litter', title: 'Szczeniaki', tagline: 'Aktualny miot', desc: 'Tylko miot — bez tworzenia hodowli.', wizard: '/wizard/miot', thumb: 'https://images.unsplash.com/photo-1552053831-71594a27632d?w=200&q=80&auto=format&fit=crop' },
  { id: 'stud', title: 'Reproduktor', tagline: 'Jeden pies', desc: 'Imię, rodowód, osiągnięcia.', wizard: '/wizard/reproduktor', thumb: 'https://images.unsplash.com/photo-1543466835-00a7907e9de1?w=200&q=80&auto=format&fit=crop' },
];

export default function HeroCreator() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isHodowca = user?.profile_type !== 'biznes';
  // creationType (granular subtype): 'kennel' | 'litter' | 'stud' | 'business'
  const [creationType, setCreationType] = useState(() => {
    if (typeof window === 'undefined') return null;
    if (user?.profile_type === 'biznes') return 'business';
    try { return window.localStorage.getItem(CREATION_TYPE_KEY) || null; } catch (_e) { return null; }
  });
  // Backend entity_type (binary): 'kennel' lub 'business'. Subtypy używamy tylko po stronie klienta do routingu.
  const entityType = creationType === 'business' ? 'business' : 'kennel';
  const [pickerOpen, setPickerOpen] = useState(false);

  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [templates, setTemplates] = useState([]);
  const [activeStep, setActiveStep] = useState(1);
  const [device, setDevice] = useState('desktop');
  const [advanced, setAdvanced] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [publishedHandoff, setPublishedHandoff] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiVibe, setAiVibe] = useState('emotional');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const debounceRef = useRef(null);
  const initialLoadedRef = useRef(false);

  // Pokaz "Co tworzysz?" przy pierwszej wizycie hodowcy (zanim wybrał typ).
  useEffect(() => {
    if (!user) return;
    if (isHodowca && !creationType) setPickerOpen(true);
  }, [user, isHodowca, creationType]);

  const handlePickCreationType = (id) => {
    setCreationType(id);
    try { window.localStorage.setItem(CREATION_TYPE_KEY, id); } catch (_e) { /* ignore */ }
    setPickerOpen(false);
  };

  // Load templates + draft on mount
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      try {
        const [tplRes, draftRes] = await Promise.all([
          axios.get(`${API}/hero-creator/templates`),
          axios.get(`${API}/hero-creator/draft?entity_type=${entityType}`, { headers: getAuthHeaders() }).catch(() => ({ data: { config: null } })),
        ]);
        if (cancelled) return;
        const tpls = tplRes.data?.templates || [];
        setTemplates(tpls);
        const existing = draftRes.data?.config;
        if (existing && existing.headline) {
          setConfig({
            image_url: existing.image_url || '',
            logo_url: existing.logo_url || '',
            label: existing.label || '',
            headline: existing.headline || '',
            subheading: existing.subheading || '',
            cta_primary: existing.cta_primary || DEFAULT_CONFIG.cta_primary,
            cta_secondary: existing.cta_secondary || null,
            style: { ...DEFAULT_CONFIG.style, ...(existing.style || {}) },
            tags: existing.tags || [],
          });
        } else if (tpls.length > 0 && tpls[0].preview_image) {
          // First-time user — preload default template's image for instant WOW.
          setConfig((c) => ({ ...c, image_url: tpls[0].preview_image }));
        }

        // Auto-prefill logo from user's primary kennel/business if not already set on draft
        try {
          const coll = entityType === 'kennel' ? 'kennels' : 'business';
          const profRes = await axios.get(`${API}/${coll}`, { headers: getAuthHeaders() }).catch(() => null);
          const items = profRes?.data?.items || profRes?.data || [];
          const primary = Array.isArray(items) ? items[0] : items;
          const ownerLogo = primary?.logo;
          if (ownerLogo) {
            setConfig((c) => (c.logo_url ? c : { ...c, logo_url: ownerLogo }));
          }
        } catch (_e) { /* prefill best-effort */ }
      } catch (e) {
        console.error('[HeroCreator] init failed', e);
      } finally {
        initialLoadedRef.current = true;
      }
    };
    init();
    return () => { cancelled = true; };
  }, [entityType]);

  // Auto-save debounced
  useEffect(() => {
    if (!initialLoadedRef.current) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        setSaving(true);
        const res = await axios.put(`${API}/hero-creator/draft`, {
          entity_type: entityType,
          ...config,
        }, { headers: getAuthHeaders() });
        setSavedAt(res.data?.saved_at || new Date().toISOString());
      } catch (e) {
        console.error('[HeroCreator] auto-save fail', e);
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [config, entityType]);

  const applyTemplate = (tpl) => {
    const preset = tpl.preset || {};
    setConfig((prev) => ({
      ...prev,
      // Apply template preview_image ONLY if user hasn't uploaded their own photo.
      image_url: prev.image_url && !prev.image_url.includes('images.unsplash.com') ? prev.image_url : (tpl.preview_image || prev.image_url),
      label: preset.label ?? prev.label,
      headline: preset.headline ?? prev.headline,
      subheading: preset.subheading ?? prev.subheading,
      cta_primary: preset.cta_primary || prev.cta_primary,
      cta_secondary: preset.cta_secondary || prev.cta_secondary,
      tags: preset.tags || prev.tags,
      style: { ...prev.style, ...(preset.style || {}), template_id: tpl.id },
    }));
    toast.success(`Zastosowano: ${tpl.vibe || tpl.name}`);
  };

  const onFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) {
      toast.error('Plik za duży (max 12MB).');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/upload`, form, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      // backend returns { urls: { large, medium, ... }, id }
      const url = res.data?.urls?.large || res.data?.urls?.original || res.data?.urls?.medium;
      if (url) {
        setConfig((c) => ({
          ...c,
          image_url: url,
          // Reset positioning for new image
          style: { ...c.style, image_position_x: 50, image_position_y: 50, image_zoom: 1.0 },
        }));
        toast.success('Zdjęcie wgrane.');
      } else {
        toast.error('Nie udało się wczytać URL zdjęcia.');
      }
    } catch (e) {
      console.error(e);
      toast.error('Błąd uploadu zdjęcia.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onLogoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error('Logo za duże (max 6MB).');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      setLogoUploading(true);
      const res = await axios.post(`${API}/upload`, form, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.urls?.medium || res.data?.urls?.original || res.data?.urls?.large;
      if (url) {
        setConfig((c) => ({ ...c, logo_url: url }));
        toast.success('Logo wgrane.');
      } else {
        toast.error('Nie udało się wczytać URL logo.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Błąd uploadu logo.');
    } finally {
      setLogoUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const publish = async () => {
    try {
      setSaving(true);
      const res = await axios.post(`${API}/hero-creator/publish`, {
        entity_type: entityType,
        ...config,
      }, { headers: getAuthHeaders() });
      const publishedSlug = res.data?.config?.entity_slug; // eslint-disable-line no-unused-vars
      setSavedAt(new Date().toISOString());
      toast.success('Wygląd strony zapisany', {
        description: 'Strona stanie się publicznie widoczna po dokończeniu profilu.',
        duration: 5000,
      });
      setPublishedHandoff(true);
    } catch (e) {
      console.error(e);
      toast.error('Błąd publikacji.');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setConfig(DEFAULT_CONFIG);
    toast.info('Przywrócono ustawienia domyślne.');
  };

  // FB-style image positioning controls
  const handlePositionChange = ({ image_position_x, image_position_y }) => {
    setConfig((c) => ({
      ...c,
      style: {
        ...c.style,
        ...(typeof image_position_x === 'number' ? { image_position_x } : {}),
        ...(typeof image_position_y === 'number' ? { image_position_y } : {}),
      },
    }));
  };
  const setImageZoom = (delta) => {
    setConfig((c) => {
      const cur = typeof c.style.image_zoom === 'number' ? c.style.image_zoom : 1.0;
      const next = Math.max(1.0, Math.min(3.0, +(cur + delta).toFixed(2)));
      return { ...c, style: { ...c.style, image_zoom: next } };
    });
  };
  const setImageZoomAbs = (val) => {
    setConfig((c) => ({ ...c, style: { ...c.style, image_zoom: Math.max(1.0, Math.min(3.0, val)) } }));
  };
  const centerImage = () => {
    setConfig((c) => ({ ...c, style: { ...c.style, image_position_x: 50, image_position_y: 50 } }));
  };
  const fitImage = () => {
    setConfig((c) => ({ ...c, style: { ...c.style, image_zoom: 1.0, image_position_x: 50, image_position_y: 50 } }));
    toast.success('Zdjęcie wyśrodkowane i dopasowane.');
  };

  const fetchAiSuggestions = async (vibe = aiVibe) => {
    try {
      setAiLoading(true);
      const res = await axios.post(`${API}/hero-creator/ai/suggest`, {
        entity_type: entityType,
        vibe,
        template_id: config.template_id || undefined,
        business_type: user?.business_type || undefined,
        current_headline: config.headline || undefined,
        current_subheading: config.subheading || undefined,
      }, { headers: getAuthHeaders() });
      setAiSuggestions(res.data || {});
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'AI niedostępne. Spróbuj ponownie.');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiHeadline = (text) => setConfig((c) => ({ ...c, headline: text }));
  const applyAiSubheading = (text) => setConfig((c) => ({ ...c, subheading: text }));
  const applyAiLabel = (text) => setConfig((c) => ({ ...c, label: text }));
  const applyAiCtaPrimary = (text) => setConfig((c) => ({ ...c, cta_primary: { ...(c.cta_primary || { href: '#' }), label: text } }));
  const applyAiCtaSecondary = (text) => setConfig((c) => ({ ...c, cta_secondary: { ...(c.cta_secondary || { href: '#' }), label: text } }));
  const applyAiTags = (tags) => setConfig((c) => ({ ...c, tags: tags.slice(0, 5) }));

  const applyAllAi = () => {
    if (!aiSuggestions) return;
    setConfig((c) => ({
      ...c,
      label: aiSuggestions.labels?.[0] || c.label,
      headline: aiSuggestions.headlines?.[0] || c.headline,
      subheading: aiSuggestions.subheadings?.[0] || c.subheading,
      cta_primary: aiSuggestions.cta_primary?.[0] ? { ...(c.cta_primary || { href: '#' }), label: aiSuggestions.cta_primary[0] } : c.cta_primary,
      cta_secondary: aiSuggestions.cta_secondary?.[0] ? { ...(c.cta_secondary || { href: '#' }), label: aiSuggestions.cta_secondary[0] } : c.cta_secondary,
      tags: aiSuggestions.tags?.length ? aiSuggestions.tags.slice(0, 5) : c.tags,
    }));
    toast.success('Zastosowano sugestie AI (1. wariant).');
  };

  const savedLabel = useMemo(() => {
    if (saving) return 'Zapisuję…';
    if (!savedAt) return 'Auto-save aktywny';
    return 'Zapisano automatycznie';
  }, [saving, savedAt]);

  const toggleTag = (preset) => {
    setConfig((c) => {
      const exists = c.tags.find((t) => t.text === preset.text);
      const tags = exists ? c.tags.filter((t) => t.text !== preset.text) : [...c.tags.slice(0, 5), preset];
      return { ...c, tags };
    });
  };

  // Topbar slot dla Hero Creator — autosave status + Reset + Publish (orange Creative Studio CTA)
  const heroTopbarSlot = (
    <div className="flex items-center gap-2 w-full justify-end" data-testid="hero-creator-topbar-slot">
      <div className="hidden sm:flex items-center gap-1.5 text-[11px] text-white/50 mr-1">
        {saving ? (
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
        ) : (
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
        )}
        <span data-testid="autosave-label">{savedLabel}</span>
      </div>
      <button
        onClick={resetToDefaults}
        className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-white/15 hover:border-white/30 text-white/80 text-xs font-medium transition-colors"
        data-testid="reset-btn">
        <RotateCcw className="w-3.5 h-3.5" /> Reset
      </button>
      <button
        onClick={publish}
        disabled={saving}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--apisai-accent-orange)] hover:bg-orange-400 text-white font-semibold text-xs shadow-lg shadow-orange-500/30 disabled:opacity-50 transition-colors"
        data-testid="publish-btn">
        <Save className="w-3.5 h-3.5" /> Opublikuj
      </button>
    </div>
  );

  return (
    <StudioShell
      mode="studio"
      contextLabel="Kreator strony"
      topbarSlot={heroTopbarSlot}
      activeKey="hero"
    >
      <div className="flex-1 min-w-0 flex flex-col text-white" data-testid="hero-creator-page">
        <Helmet><title>Kreator pierwszego wrażenia — APISAI</title></Helmet>
        <HeroCreatorIntro />
        <NextStepGuide
          open={publishedHandoff}
          currentStep={1}
          userName={user?.name}
          creationType={creationType}
          entityType={entityType}
          onClose={() => setPublishedHandoff(false)}
        />

        {/* CO TWORZYSZ? — modal pierwszego wyboru przy starcie Creative Studio (tylko hodowcy) */}
        {pickerOpen && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="creation-type-picker">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
            <div className="relative w-full max-w-lg rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-[0_40px_120px_-30px_rgba(0,0,0,0.85)]"
              style={{ background: 'linear-gradient(160deg, #04202F 0%, #052B3D 60%, #073447 100%)' }}>
              <div className="pointer-events-none absolute -top-28 -right-28 w-72 h-72 rounded-full opacity-40"
                style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.45), transparent 65%)' }}
                aria-hidden="true"
              />
              <div className="relative p-7 sm:p-8">
                <div className="inline-flex items-center gap-1.5 text-emerald-300/90 text-[10px] font-bold uppercase tracking-[0.2em] mb-3">
                  <Check className="w-3 h-3" strokeWidth={3} />
                  Krok 1 · Co tworzysz?
                </div>
                <h3 className="text-white text-xl sm:text-2xl font-bold tracking-tight leading-tight mb-2">
                  Zanim zaczniemy projektować…
                </h3>
                <p className="text-white/55 text-sm leading-relaxed mb-6">
                  Wybierz, co chcesz pokazać klientom. Od tego zależy układ strony i przyszły wizard.
                </p>
                <div className="space-y-2.5" data-testid="creation-type-options">
                  {CREATION_TYPES_HODOWCA.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handlePickCreationType(t.id)}
                      data-testid={`creation-type-${t.id}`}
                      className="w-full text-left flex items-center gap-4 p-3.5 rounded-2xl bg-white/[0.04] hover:bg-white/[0.08] ring-1 ring-white/8 hover:ring-[var(--apisai-primary)]/60 transition-all hover:-translate-y-0.5"
                    >
                      <div className="flex-shrink-0 w-14 h-14 rounded-xl overflow-hidden ring-1 ring-white/10">
                        <img src={t.thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[var(--apisai-primary)] text-[10px] font-bold uppercase tracking-[0.16em]">{t.tagline}</p>
                        <p className="text-white text-base font-semibold leading-tight">{t.title}</p>
                        <p className="text-white/55 text-[12.5px] leading-snug mt-0.5">{t.desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-white/35 flex-shrink-0" />
                    </button>
                  ))}
                </div>
                <p className="text-white/35 text-[11px] mt-5 leading-relaxed text-center">
                  Później w panelu możesz dodać kolejne profile — np. zacząć od reproduktora, a hodowlę dorobić.
                </p>
              </div>
            </div>
          </div>
        )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Sub-info bar */}
        <div className="h-12 px-5 sm:px-7 border-b border-white/[0.06] flex items-center gap-3">
          <button onClick={() => navigate('/panel')} className="text-zinc-400 hover:text-white" data-testid="back-to-panel">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="min-w-0">
            <div className="text-[11px] text-zinc-500 truncate">Stwórz profesjonalny wygląd swojej marki. Mobile-safe.</div>
          </div>
        </div>

        {/* TEMPLATE HERO STRIP — Template-First Experience */}
        <div className="border-b border-white/[0.06] bg-gradient-to-b from-[#04202F] to-[#03192A]" data-testid="templates-hero-strip">
          <div className="px-5 sm:px-7 py-5">
            <div className="flex items-end justify-between mb-3.5">
              <div>
                <div className="text-xl sm:text-2xl font-bold tracking-tight">Wybierz styl swojej strony</div>
                <div className="text-sm text-zinc-400 mt-0.5">Gotowe projekty dla hodowli i biznesów.</div>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto scrollbar-hide -mx-1 px-1 pb-1" data-testid="templates-list">
              {templates.map((tpl) => {
                const active = config.style?.template_id === tpl.id;
                const accent = tpl.preset?.style?.accent_color || tpl.preview_color || '#F97316';
                const variant = tpl.card_variant || 'classic';
                // Variant-specific composition (gradient, alignment, accent line, font)
                const overlayCss = ({
                  cinematic: 'linear-gradient(to top, rgba(0,0,0,0.95) 0%, rgba(0,0,0,0.4) 38%, rgba(0,0,0,0.55) 100%)',
                  warm: 'linear-gradient(to top, rgba(36,18,8,0.92) 0%, rgba(40,16,4,0.35) 55%, rgba(0,0,0,0) 95%)',
                  luxury: 'linear-gradient(to top, rgba(8,6,2,0.95) 0%, rgba(40,30,8,0.5) 50%, rgba(0,0,0,0.1) 100%)',
                  clean: 'linear-gradient(to top, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.1) 60%, rgba(0,0,0,0) 100%)',
                  minimal: 'linear-gradient(to top, rgba(8,8,18,0.85) 0%, rgba(0,0,0,0.15) 70%, rgba(0,0,0,0) 100%)',
                  classic: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.45) 45%, rgba(0,0,0,0) 80%)',
                })[variant];
                const isLuxury = variant === 'luxury';
                const isWarm = variant === 'warm';
                const isClean = variant === 'clean';
                const alignClass = isWarm ? 'text-center items-center' : 'text-left items-start';
                const fontFam = isLuxury ? "'Playfair Display', Georgia, serif" : "'Montserrat', system-ui, sans-serif";
                return (
                  <button key={tpl.id} onClick={() => applyTemplate(tpl)}
                    className={`group relative flex-shrink-0 w-[230px] sm:w-[260px] h-[160px] rounded-2xl overflow-hidden border-2 transition-all duration-300 ${active ? 'border-orange-500 ring-4 ring-orange-500/20 scale-[1.02]' : 'border-white/10 hover:border-white/25 hover:scale-[1.01]'}`}
                    style={{ boxShadow: active ? `0 24px 50px -12px ${accent}55` : undefined }}
                    data-testid={`template-${tpl.id}`}>
                    {tpl.preview_image ? (
                      <img src={tpl.preview_image} alt={tpl.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 to-black" />
                    )}
                    {/* Cinematic gets dual gradient (top + bottom) */}
                    {variant === 'cinematic' && (
                      <div className="absolute inset-x-0 top-0 h-16" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)' }} />
                    )}
                    <div className="absolute inset-0" style={{ background: overlayCss }} />
                    {/* Luxury: double-line gold frame */}
                    {isLuxury && (
                      <div className="absolute inset-2 border border-yellow-500/30 rounded-xl pointer-events-none" />
                    )}
                    {/* Active badge / kind badge */}
                    <div className="absolute top-2 right-2 z-10">
                      {active ? (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500 text-white text-[10px] font-bold shadow-lg">
                          <Check className="w-3 h-3" /> Aktywny
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur text-white/70 text-[10px] font-medium border border-white/15">
                          {tpl.kind === 'breeder' ? 'Hodowla' : 'Biznes'}
                        </div>
                      )}
                    </div>
                    {/* Text content — per-variant alignment & font */}
                    <div className={`absolute inset-x-0 ${isClean ? 'top-0 pt-3' : 'bottom-0'} p-3 flex flex-col ${alignClass}`}>
                      <div className="uppercase tracking-[0.16em] text-[9px] font-bold mb-0.5" style={{ color: accent }}>{tpl.vibe}</div>
                      <div className="text-sm sm:text-base font-bold text-white truncate leading-tight" style={{ fontFamily: fontFam }}>{tpl.name}</div>
                    </div>
                    {/* Color accent line — variant specific */}
                    {variant !== 'clean' && (
                      <div className="absolute bottom-0 left-0 right-0" style={{ backgroundColor: accent, height: isLuxury ? 4 : 3 }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* MAIN BODY: compact sidebar + dominant preview */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[320px_1fr] min-h-0">
          {/* LEFT — steps + active step controls */}
          <div className="border-r border-white/[0.06] bg-[#04202F] overflow-y-auto" data-testid="creator-left-sidebar">
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-3">Kroki</div>
              <div className="space-y-1.5">
                {STEPS.map((s) => {
                  const active = activeStep === s.id;
                  return (
                    <button
                      key={s.id}
                      onClick={() => setActiveStep(s.id)}
                      className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition ${active ? 'border-orange-500/40 bg-orange-500/10' : 'border-transparent hover:bg-white/[0.04]'}`}
                      data-testid={`step-${s.id}`}>
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${active ? 'bg-orange-500 text-white' : 'bg-white/[0.05] text-zinc-400'}`}>
                        {s.id}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[13px] font-semibold text-white truncate">{s.title}</div>
                        <div className="text-[11px] text-zinc-500 truncate">{s.sub}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active step body */}
            <div className="px-5 pb-8 border-t border-white/[0.06] pt-5" data-testid="active-step-body">
              {activeStep === 1 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-3">Zdjęcie tła</div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex flex-col items-center justify-center gap-2 py-7 px-3 rounded-xl border-2 border-dashed border-white/15 hover:border-orange-500/50 hover:bg-white/[0.02] transition disabled:opacity-50"
                    data-testid="upload-btn">
                    <Upload className="w-5 h-5 text-orange-300" />
                    <div className="text-sm text-zinc-200 font-medium">{uploading ? 'Wgrywam…' : 'Wgraj swoje zdjęcie'}</div>
                    <div className="text-[11px] text-zinc-500">PNG, JPG do 12MB</div>
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={onFileSelect} className="hidden" />
                  {config.image_url && (
                    <div className="mt-3 text-[11px] text-emerald-300 flex items-center gap-1.5">
                      <Check className="w-3.5 h-3.5" /> Zdjęcie aktywne
                    </div>
                  )}

                  {/* LOGO MARKI — kwadratowe, overlay top-left w preview */}
                  <div className="mt-5 pt-5 border-t border-white/[0.06]" data-testid="logo-panel">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Logo marki <span className="normal-case font-normal text-zinc-600">(opcjonalnie)</span></div>
                    <div className="text-[11px] text-zinc-400 leading-snug mb-3">
                      Wyświetlamy je w lewym górnym rogu sekcji hero — buduje rozpoznawalność marki.
                    </div>
                    <input ref={logoInputRef} type="file" accept="image/*" onChange={onLogoSelect} className="hidden" />
                    {config.logo_url ? (
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-xl bg-white/95 ring-1 ring-white/20 shadow-md overflow-hidden flex items-center justify-center p-1.5 flex-shrink-0">
                          <img src={config.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                        <div className="flex-1 flex flex-col gap-1.5">
                          <button
                            onClick={() => logoInputRef.current?.click()}
                            disabled={logoUploading}
                            className="px-3 py-1.5 rounded-lg bg-white/[0.06] hover:bg-white/[0.12] text-zinc-200 text-[11px] font-medium border border-white/10 transition disabled:opacity-50"
                            data-testid="logo-change-btn">
                            {logoUploading ? 'Wgrywam…' : 'Zmień logo'}
                          </button>
                          <button
                            onClick={() => setConfig((c) => ({ ...c, logo_url: '' }))}
                            className="px-3 py-1.5 rounded-lg text-red-300 hover:bg-red-500/10 text-[11px] font-medium border border-red-500/20 transition"
                            data-testid="logo-remove-btn">
                            Usuń logo
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoUploading}
                        className="w-full flex flex-col items-center justify-center gap-1.5 py-5 px-3 rounded-xl border-2 border-dashed border-white/15 hover:border-orange-500/50 hover:bg-white/[0.02] transition disabled:opacity-50"
                        data-testid="logo-upload-btn">
                        <Upload className="w-4 h-4 text-orange-300" />
                        <div className="text-[12px] text-zinc-200 font-medium">{logoUploading ? 'Wgrywam logo…' : 'Wgraj logo'}</div>
                        <div className="text-[10px] text-zinc-500">PNG z przezroczystym tłem · do 6MB</div>
                      </button>
                    )}
                  </div>

                  {/* DOPASUJ ZDJĘCIE — FB-style positioning */}
                  {config.image_url && (
                    <div className="mt-5 pt-5 border-t border-white/[0.06]" data-testid="adjust-image-panel">
                      <div className="flex items-center gap-2 mb-2">
                        <Move className="w-3.5 h-3.5 text-orange-300" />
                        <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Dopasuj zdjęcie</div>
                      </div>
                      <div className="text-[11px] text-zinc-400 leading-snug mb-3">
                        Przesuń zdjęcie, aby najważniejszy element był dobrze widoczny.
                      </div>

                      {/* Zoom control */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="text-[10px] uppercase tracking-wider text-zinc-500">Powiększenie</div>
                          <div className="text-[11px] text-zinc-300 font-mono">{((config.style?.image_zoom ?? 1.0)).toFixed(1)}×</div>
                        </div>
                        <input type="range" min={1.0} max={3.0} step={0.1}
                          value={config.style?.image_zoom ?? 1.0}
                          onChange={(e) => setImageZoomAbs(parseFloat(e.target.value))}
                          className="w-full accent-orange-500"
                          data-testid="image-zoom-slider"
                        />
                      </div>

                      {/* Buttons grid */}
                      <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setImageZoom(0.2)}
                          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 text-zinc-200 text-[11px] font-medium transition"
                          data-testid="image-zoom-in">
                          <ZoomIn className="w-3.5 h-3.5" /> Przybliż
                        </button>
                        <button onClick={() => setImageZoom(-0.2)}
                          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 text-zinc-200 text-[11px] font-medium transition"
                          data-testid="image-zoom-out">
                          <ZoomOut className="w-3.5 h-3.5" /> Oddal
                        </button>
                        <button onClick={centerImage}
                          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 text-zinc-200 text-[11px] font-medium transition"
                          data-testid="image-center">
                          <Crosshair className="w-3.5 h-3.5" /> Wyśrodkuj
                        </button>
                        <button onClick={fitImage}
                          className="inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-orange-500/40 hover:bg-orange-500/5 text-zinc-200 text-[11px] font-medium transition"
                          data-testid="image-fit">
                          <Maximize2 className="w-3.5 h-3.5" /> Dopasuj
                        </button>
                      </div>

                      <div className="mt-3 text-[10px] text-zinc-500 leading-snug flex items-start gap-1.5">
                        <Move className="w-3 h-3 mt-0.5 flex-shrink-0 text-orange-300" />
                        <span>Przeciągnij zdjęcie w podglądzie — myszką lub palcem na telefonie.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeStep === 2 && (
                <div className="space-y-3.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Tekst</div>
                    <button
                      onClick={() => { setAiOpen((o) => !o); if (!aiSuggestions && !aiOpen) fetchAiSuggestions(); }}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gradient-to-r from-orange-600 to-orange-600 hover:from-orange-500 hover:to-orange-500 text-white text-[11px] font-semibold shadow-lg shadow-orange-500/30 transition"
                      data-testid="ai-helper-toggle">
                      <Sparkles className="w-3 h-3" /> {aiOpen ? 'Ukryj AI' : 'Zaproponuj AI'}
                    </button>
                  </div>

                  {aiOpen && (
                    <div className="rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-950/30 to-orange-950/20 p-3 space-y-3" data-testid="ai-helper-panel">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-orange-300 font-semibold mb-1.5">Tone of voice</div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {[
                            { id: 'emotional', label: 'Emocjonalny' },
                            { id: 'professional', label: 'Profesjonalny' },
                            { id: 'champion', label: 'Championski' },
                            { id: 'family', label: 'Rodzinny' },
                            { id: 'luxury', label: 'Luksusowy' },
                          ].map((v) => (
                            <button key={v.id}
                              onClick={() => { setAiVibe(v.id); fetchAiSuggestions(v.id); }}
                              className={`px-1.5 py-1 rounded-md text-[10px] font-medium border transition ${aiVibe === v.id ? 'border-orange-400 bg-orange-500/20 text-white' : 'border-white/10 text-zinc-400 hover:border-white/25'}`}
                              data-testid={`ai-vibe-${v.id}`}>
                              {v.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2">
                        <button
                          onClick={() => fetchAiSuggestions()}
                          disabled={aiLoading}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md bg-orange-600 hover:bg-orange-500 text-white text-[11px] font-semibold disabled:opacity-50"
                          data-testid="ai-generate-btn">
                          {aiLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                          {aiLoading ? 'Generuję…' : aiSuggestions ? 'Wygeneruj ponownie' : 'Wygeneruj sugestie'}
                        </button>
                        {aiSuggestions && (
                          <button
                            onClick={applyAllAi}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold"
                            data-testid="ai-apply-all-btn">
                            <Check className="w-3 h-3" /> Zastosuj 1. wariant
                          </button>
                        )}
                      </div>

                      {aiSuggestions && (
                        <div className="space-y-2.5 max-h-[420px] overflow-y-auto pr-1" data-testid="ai-suggestions">
                          <AiBlock title="Etykieta" items={aiSuggestions.labels || []} onApply={applyAiLabel} testidPrefix="ai-label" />
                          <AiBlock title="Nagłówek" items={aiSuggestions.headlines || []} onApply={applyAiHeadline} testidPrefix="ai-headline" />
                          <AiBlock title="Podtytuł" items={aiSuggestions.subheadings || []} onApply={applyAiSubheading} testidPrefix="ai-sub" multiline />
                          <AiBlock title="Przycisk główny" items={aiSuggestions.cta_primary || []} onApply={applyAiCtaPrimary} testidPrefix="ai-cta-primary" />
                          <AiBlock title="Przycisk dodatkowy" items={aiSuggestions.cta_secondary || []} onApply={applyAiCtaSecondary} testidPrefix="ai-cta-secondary" />
                          {(aiSuggestions.tags || []).length > 0 && (
                            <div>
                              <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">Tagi (AI)</div>
                              <button
                                onClick={() => applyAiTags(aiSuggestions.tags)}
                                className="w-full text-left text-[11px] text-zinc-300 bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-orange-400/40 rounded-lg px-2 py-1.5 transition"
                                data-testid="ai-tags-apply">
                                <span className="text-orange-300 font-semibold">Zastosuj zestaw: </span>
                                {aiSuggestions.tags.map((t) => t.text).join(' · ')}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <Field label="Etykieta (mały tekst nad nagłówkiem)" data-testid-input="input-label">
                    <input type="text" value={config.label} onChange={(e) => setConfig((c) => ({ ...c, label: e.target.value }))}
                      maxLength={60} className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500/50 focus:outline-none"
                      data-testid="input-label" />
                  </Field>
                  <Field label="Nagłówek (duży tekst)">
                    <textarea rows={2} value={config.headline} onChange={(e) => setConfig((c) => ({ ...c, headline: e.target.value }))}
                      maxLength={120} className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500/50 focus:outline-none resize-none"
                      data-testid="input-headline" />
                  </Field>
                  <Field label="Podtytuł (krótki opis)">
                    <textarea rows={3} value={config.subheading} onChange={(e) => setConfig((c) => ({ ...c, subheading: e.target.value }))}
                      maxLength={200} className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500/50 focus:outline-none resize-none"
                      data-testid="input-subheading" />
                  </Field>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Przycisk główny">
                      <input type="text" value={config.cta_primary?.label || ''} onChange={(e) => setConfig((c) => ({ ...c, cta_primary: { ...(c.cta_primary || { href: '#' }), label: e.target.value } }))}
                        className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500/50 focus:outline-none"
                        data-testid="input-cta-primary" />
                    </Field>
                    <Field label="Przycisk dodatkowy">
                      <input type="text" value={config.cta_secondary?.label || ''} onChange={(e) => setConfig((c) => ({ ...c, cta_secondary: e.target.value ? { ...(c.cta_secondary || { href: '#' }), label: e.target.value } : null }))}
                        className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-orange-500/50 focus:outline-none"
                        data-testid="input-cta-secondary" />
                    </Field>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Tagi (max 3 widoczne)</div>
                    <div className="flex flex-wrap gap-1.5">
                      {TAG_PRESETS.map((tp) => {
                        const isOn = config.tags.find((t) => t.text === tp.text);
                        return (
                          <button key={tp.text}
                            onClick={() => toggleTag(tp)}
                            className={`text-[11px] px-2.5 py-1 rounded-full border transition ${isOn ? 'border-orange-500/60 bg-orange-500/15 text-orange-200' : 'border-white/15 text-zinc-300 hover:border-white/30'}`}
                            data-testid={`tag-toggle-${tp.text}`}>
                            {isOn ? '✓ ' : '+ '}{tp.text}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {activeStep === 3 && (
                <div className="space-y-4">
                  <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Styl</div>
                  <div>
                    <div className="text-[11px] text-zinc-400 mb-1.5">Akcent (kolor)</div>
                    <div className="flex flex-wrap gap-2">
                      {ACCENT_COLORS.map((col) => (
                        <button key={col}
                          onClick={() => setConfig((c) => ({ ...c, style: { ...c.style, accent_color: col } }))}
                          className={`w-7 h-7 rounded-lg border-2 ${config.style.accent_color === col ? 'border-white' : 'border-white/15'}`}
                          style={{ backgroundColor: col }} data-testid={`accent-${col}`} aria-label={col} />
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] text-zinc-400 mb-1.5">Nakładka na zdjęcie</div>
                    <div className="grid grid-cols-2 gap-2">
                      {OVERLAY_OPTIONS.map((ov) => (
                        <button key={ov.id}
                          onClick={() => setConfig((c) => ({ ...c, style: { ...c.style, overlay: ov.id } }))}
                          className={`relative h-12 rounded-lg border overflow-hidden ${config.style.overlay === ov.id ? 'border-orange-500 ring-2 ring-orange-500/30' : 'border-white/15'}`}
                          data-testid={`overlay-${ov.id}`}>
                          <div className="absolute inset-0" style={{ background: ov.preview === 'transparent' ? 'repeating-linear-gradient(45deg,#222,#222 4px,#333 4px,#333 8px)' : ov.preview }} />
                          <div className="absolute inset-0 flex items-end p-1.5">
                            <span className="text-[10px] text-white font-medium drop-shadow">{ov.label}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] text-zinc-400 mb-1.5">Pozycja tekstu</div>
                    <div className="grid grid-cols-3 gap-2">
                      {['left', 'center', 'right'].map((a) => (
                        <button key={a}
                          onClick={() => setConfig((c) => ({ ...c, style: { ...c.style, text_align: a } }))}
                          className={`py-2 rounded-lg border text-xs font-medium ${config.style.text_align === a ? 'border-orange-500 bg-orange-500/10 text-white' : 'border-white/15 text-zinc-400 hover:border-white/30'}`}
                          data-testid={`align-${a}`}>
                          {a === 'left' ? 'Lewo' : a === 'center' ? 'Środek' : 'Prawo'}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setAdvanced((a) => !a)}
                    className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-white/10 hover:border-orange-500/40 text-xs text-zinc-300"
                    data-testid="advanced-toggle">
                    <Wand2 className="w-3.5 h-3.5 text-orange-300" />
                    {advanced ? 'Ukryj zaawansowane' : 'Pokaż zaawansowane'}
                  </button>

                  {advanced && (
                    <div className="space-y-4 pt-2 border-t border-white/5" data-testid="advanced-controls">
                      <div>
                        <div className="text-[11px] text-zinc-400 mb-1.5">Czcionka nagłówka</div>
                        <select value={config.style.headline_font}
                          onChange={(e) => setConfig((c) => ({ ...c, style: { ...c.style, headline_font: e.target.value } }))}
                          className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"
                          data-testid="font-select">
                          {FONT_OPTIONS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                        </select>
                      </div>
                      <SliderRow label="Wielkość nagłówka" value={config.style.headline_size} min={32} max={108} onChange={(v) => setConfig((c) => ({ ...c, style: { ...c.style, headline_size: v } }))} testid="size-headline" />
                      <SliderRow label="Wielkość podtytułu" value={config.style.sub_size} min={14} max={36} onChange={(v) => setConfig((c) => ({ ...c, style: { ...c.style, sub_size: v } }))} testid="size-sub" />
                      <SliderRow label="Siła nakładki" value={config.style.overlay_strength} min={0} max={100} onChange={(v) => setConfig((c) => ({ ...c, style: { ...c.style, overlay_strength: v } }))} testid="overlay-strength" />
                      <div>
                        <div className="text-[11px] text-zinc-400 mb-1.5">Styl przycisku</div>
                        <div className="grid grid-cols-3 gap-2">
                          {['filled', 'outline', 'pill'].map((cs) => (
                            <button key={cs}
                              onClick={() => setConfig((c) => ({ ...c, style: { ...c.style, cta_style: cs } }))}
                              className={`py-2 rounded-lg border text-[11px] font-medium ${config.style.cta_style === cs ? 'border-orange-500 bg-orange-500/10 text-white' : 'border-white/15 text-zinc-400 hover:border-white/30'}`}
                              data-testid={`cta-style-${cs}`}>
                              {cs === 'filled' ? 'Wypełniony' : cs === 'outline' ? 'Obrysowany' : 'Pigułka'}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeStep === 4 && (
                <div className="space-y-3" data-testid="finish-step">
                  <div className="rounded-xl p-4 border border-emerald-500/30 bg-emerald-500/5">
                    <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm mb-2">
                      <Eye className="w-4 h-4" /> Gotowe do publikacji
                    </div>
                    <div className="text-xs text-zinc-400 leading-relaxed">
                      Sprawdź podgląd desktop i mobile. Gdy wszystko wygląda jak chcesz — kliknij Opublikuj u góry.
                    </div>
                  </div>
                  <div className="rounded-xl p-4 border border-orange-500/30 bg-orange-500/5">
                    <div className="text-xs text-orange-200 font-semibold mb-2">Po publikacji widoczny w:</div>
                    <div className="text-[11px] text-zinc-400 leading-relaxed">
                      Google · Bing · Facebook · Katalog APISAI · AI Search
                    </div>
                  </div>
                  <button onClick={publish} disabled={saving}
                    className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl bg-orange-500 hover:bg-orange-400 text-white font-semibold disabled:opacity-50"
                    data-testid="finish-publish-btn">
                    <Save className="w-4 h-4" /> Opublikuj top sekcji
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* CENTER — DOMINANT live preview with premium ambient glow */}
          <div className="relative bg-[#03192A] overflow-y-auto" data-testid="creator-center">
            {/* Ambient glow background */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
              <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[80%] h-[60%] rounded-full opacity-40 blur-[100px]"
                style={{ background: `radial-gradient(circle, ${config.style?.accent_color || '#F97316'} 0%, transparent 70%)` }} />
              <div className="absolute bottom-0 right-1/4 w-[40%] h-[40%] rounded-full bg-orange-500/15 blur-[120px]" />
            </div>

            <div className="relative p-6 sm:p-10 max-w-[1400px] mx-auto">
              {/* Banner: Zapisany wygląd (pojawia się po pierwszym save) */}
              {savedAt && (
                <div
                  className="mb-5 flex items-center justify-between gap-4 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-500/25 px-4 py-2.5"
                  data-testid="hero-saved-banner"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" strokeWidth={3} />
                    <p className="text-emerald-100 text-[13px] font-medium leading-tight truncate">
                      <span className="font-semibold">Ten wygląd jest zapisany.</span>
                      <span className="text-emerald-200/70 ml-1.5">Tak zobaczy Cię klient po dokończeniu profilu.</span>
                    </p>
                  </div>
                  <button
                    onClick={() => navigate('/panel/moje')}
                    className="hidden sm:inline-flex items-center gap-1.5 text-emerald-200 hover:text-white text-[12px] font-semibold transition-colors flex-shrink-0"
                    data-testid="hero-saved-cta"
                  >
                    Twoje strony →
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-base sm:text-xl font-bold text-white tracking-tight">Tak zobaczy Cię klient</div>
                  <div className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Podgląd na żywo
                  </div>
                </div>
                <div className="inline-flex rounded-xl border border-white/10 p-1 bg-white/[0.04] backdrop-blur">
                  <button onClick={() => setDevice('desktop')}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition ${device === 'desktop' ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'text-zinc-400 hover:text-white'}`}
                    data-testid="device-desktop">
                    <Monitor className="w-3.5 h-3.5" /> Komputer
                  </button>
                  <button onClick={() => setDevice('mobile')}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-semibold transition ${device === 'mobile' ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30' : 'text-zinc-400 hover:text-white'}`}
                    data-testid="device-mobile">
                    <Smartphone className="w-3.5 h-3.5" /> Telefon
                  </button>
                </div>
              </div>

              {device === 'mobile' ? (
                /* Mobile device frame mockup */
                <div className="mx-auto" style={{ maxWidth: 340 }}>
                  <div className="relative mx-auto rounded-[44px] p-2.5 bg-gradient-to-b from-zinc-900 to-black border border-white/15 shadow-2xl shadow-black/70" style={{ boxShadow: `0 40px 100px -20px ${config.style?.accent_color || '#F97316'}55, 0 20px 60px -10px rgba(0,0,0,0.8)` }}>
                    <div className="absolute top-3.5 left-1/2 -translate-x-1/2 w-24 h-5 bg-black rounded-full z-10 border border-white/10" />
                    <div className="rounded-[34px] overflow-hidden">
                      <HeroPreview config={config} device="mobile" draggable={!!config.image_url} onPositionChange={handlePositionChange} />
                    </div>
                  </div>
                </div>
              ) : (
                /* Desktop: huge wide preview */
                <div className="mx-auto relative" style={{ maxWidth: 1280 }}>
                  <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ boxShadow: `0 50px 120px -25px ${config.style?.accent_color || '#F97316'}55, 0 30px 80px -10px rgba(0,0,0,0.7)` }}>
                    <HeroPreview config={config} device="desktop" draggable={!!config.image_url} onPositionChange={handlePositionChange} />
                  </div>
                  {config.image_url && (
                    <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-md bg-black/55 backdrop-blur text-[10px] text-white/85 font-medium border border-white/10 flex items-center gap-1.5 pointer-events-none">
                      <Move className="w-3 h-3 text-orange-300" /> Przeciągnij aby dopasować
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-[11px] text-zinc-500">
                <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5 text-orange-300" /> Wygląd na telefonie pod kontrolą</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> Automatyczny kontrast i odstępy</span>
                <span className="inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" /> Przyciski w słupku na telefonie</span>
              </div>
            </div>
          </div>
        </div>
      </main>
      </div>
    </StudioShell>
  );
}

/* ─── HELPERS ─── */
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function SliderRow({ label, value, min, max, onChange, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] text-zinc-400">{label}</div>
        <div className="text-[11px] text-zinc-300 font-mono">{value}</div>
      </div>
      <input type="range" min={min} max={max} value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full accent-orange-500"
        data-testid={testid}
      />
    </div>
  );
}

function AiBlock({ title, items, onApply, testidPrefix, multiline = false }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div data-testid={`${testidPrefix}-block`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-1">{title}</div>
      <div className="space-y-1">
        {items.map((it, i) => (
          <button
            key={i}
            onClick={() => onApply(it)}
            className={`block w-full text-left text-[11px] text-zinc-200 bg-white/[0.04] hover:bg-orange-500/15 border border-white/10 hover:border-orange-400/40 rounded-lg px-2 py-1.5 transition ${multiline ? 'leading-snug' : 'truncate'}`}
            data-testid={`${testidPrefix}-${i}`}
            title={it}>
            <span className="text-orange-300 font-semibold mr-1">{i + 1}.</span>{it}
          </button>
        ))}
      </div>
    </div>
  );
}
